import { MetricSparkline, type MetricStatus, type SparklinePoint } from './MetricSparkline.js';

interface SparklineConfig {
  data: SparklinePoint[];
  kind: 'line' | 'bar';
  baseline?: number;
}

interface MetricCardProps {
  name: string;
  baseline: number | null;
  current30d: number | null;
  delta: number | null;
  status?: MetricStatus;
  sparkline?: SparklineConfig;
  format?: (v: number | null) => string;
  /** Hover help shown via native title= tooltip on a small (i) glyph next to the name. */
  helpText?: string;
  /** Tightens padding + fonts for dense layouts (e.g. per-repo dashboard single-row). */
  compact?: boolean;
}

export interface PillStyle {
  background: string;
  color: string;
}

export const STATUS_PILL: Record<MetricStatus, PillStyle> = {
  green:  { background: 'var(--loop-green-100)',  color: 'var(--loop-green-700)'  },
  yellow: { background: 'var(--loop-yellow-100)', color: 'var(--loop-yellow-800)' },
  red:    { background: 'var(--loop-red-100)',    color: 'var(--loop-red-700)'    },
};

export function fallbackPill(delta: number | null): PillStyle {
  if (delta === null) return { background: 'var(--loop-neutral-200)', color: 'var(--loop-neutral-700)' };
  if (delta <= 0)     return { background: 'var(--loop-green-100)',   color: 'var(--loop-green-700)'  };
  if (delta <= 10)    return { background: 'var(--loop-yellow-100)',  color: 'var(--loop-yellow-800)' };
  return                       { background: 'var(--loop-red-100)',     color: 'var(--loop-red-700)'    };
}

export function pickPill(status: MetricStatus | undefined, delta: number | null): PillStyle {
  return status ? STATUS_PILL[status] : fallbackPill(delta);
}

function fmt(v: number | null): string {
  if (v === null) return '—';
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function MetricCard({ name, baseline, current30d, delta, status, sparkline, format, helpText, compact }: MetricCardProps) {
  const arrow = delta === null ? '' : delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
  const pill = pickPill(status, delta);
  const display = format ?? fmt;

  return (
    <div
      className="card"
      style={{
        marginBottom: 0,
        display: 'flex',
        flexDirection: 'column',
        ...(compact ? { padding: 'var(--loop-space-150)' } : {}),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 'var(--loop-space-050, 4px)' }}>
        <div
          style={{
            fontSize: compact ? '0.75rem' : '0.875rem',
            fontWeight: 528,
            color: 'var(--loop-neutral-900)',
            whiteSpace: compact ? 'nowrap' : undefined,
            overflow: compact ? 'hidden' : undefined,
            textOverflow: compact ? 'ellipsis' : undefined,
            minWidth: 0,
          }}
        >
          {name}
        </div>
        {helpText && (
          <span
            role="img"
            aria-label={helpText}
            title={helpText}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 14,
              height: 14,
              color: 'var(--loop-neutral-500)',
              cursor: 'help',
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </span>
        )}
      </div>
      {/* flexWrap + rowGap so an over-wide value (e.g. a large token delta)
          drops the pill to the next line instead of overflowing the tile;
          minWidth:0 lets the value columns shrink rather than force overflow. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: compact ? 8 : 12, flexWrap: 'wrap', rowGap: 'var(--loop-space-050, 4px)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: compact ? '0.6875rem' : '0.75rem', color: 'var(--loop-neutral-600)', lineHeight: compact ? 1.1 : undefined }}>Baseline</div>
          <div style={{ fontSize: compact ? '1rem' : '1.125rem', fontWeight: 664, color: 'var(--loop-neutral-1100)', lineHeight: compact ? 1.15 : undefined }}>{display(baseline)}</div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: compact ? '0.6875rem' : '0.75rem', color: 'var(--loop-neutral-600)', lineHeight: compact ? 1.1 : undefined }}>Last 30d</div>
          <div style={{ fontSize: compact ? '1rem' : '1.125rem', fontWeight: 664, color: 'var(--loop-neutral-1100)', lineHeight: compact ? 1.15 : undefined }}>{display(current30d)}</div>
        </div>
        {delta !== null && (
          <span
            style={{
              marginLeft: 'auto',
              display: 'inline-block',
              whiteSpace: 'nowrap',
              padding: '2px 8px',
              borderRadius: '999px',
              fontSize: '0.75rem',
              fontWeight: 528,
              background: pill.background,
              color: pill.color
            }}
          >
            {arrow} {display(Math.abs(delta))}
          </span>
        )}
      </div>
      {sparkline && (
        <div style={{ marginTop: 'var(--loop-space-100)' }}>
          <MetricSparkline
            data={sparkline.data}
            kind={sparkline.kind}
            status={status}
            baseline={sparkline.baseline}
            ariaLabel={`${name.replace(/\s*\([^)]*\)\s*$/, '')} 90-day trend`}
          />
        </div>
      )}
    </div>
  );
}
